import { Collapsible } from '@ui/animated/Collapsible'
import ConfigDebugModal from 'features/debug/ConfigDebugModal'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import { useToast } from '@ui/toasts/useToastManager'
import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import DealsModal from '@order/modals/Deals/DealsModal'
import useBundleType from '@order/hooks/useBundleType'
import useCancelExtraResults from '@order/hooks/useCancelExtraResults'
import { useActiveDeal } from '@order/hooks/useDeal'
import { useOrderState } from '@order/hooks/useOrderState'
import useStartOrderConfig from '@order/hooks/useStartOrderConfig'
import { captureException } from '@sentry/react-native'
import { FlashList, FlashListRef } from '@shopify/flash-list'
import { trpc } from 'api'
import { router, useNavigation } from 'expo-router'
import useAvatarFlow from 'features/avatar/useAvatarFlow'
import useFlattenInfiniteData from 'hooks/useFlattenInfiniteData'
import { useIsFocused } from '@ui/hooks/useIsFocused'
import { useRefreshByUser } from 'hooks/useRefreshByUser'
import useSession, { useCustomer } from 'hooks/useSession'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import { getCurrentFrameIndex, useFrameActions } from '@frame/useFrameActions'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import useModalDataState from '@order/hooks/useModalDataState'
import AnimatedEmpty from '@ui/animated/AnimatedEmpty'
import Item from './Item'
import Search from './Search'
import useOrderValues from '@order/hooks/values/useOrderValues'

export default function FrameSelect() {
  const { t } = useTranslation(['ui', 'frame', 'modals'])
  const text = 'frame:select'

  const focused = useIsFocused()
  const customer = useCustomer()
  const { bundleType } = useBundleType()
  const orderValues = useOrderValues({ bundleType })
  const navigation = useNavigation()
  const removeByBundle = useOrderState((s) => s.removeByBundle)
  const resetItemCompleted = useOrderState((s) => s.resetItemCompleted)
  const activeDeal = useActiveDeal()
  const { isAvatarFlow } = useAvatarFlow()

  const [dealModal, setDealModal] = useState(() => {
    const { deals } = useSession.getState()
    return !(deals.upgrade || deals.base)
  })
  const setDeal = useSession((s) => s.setDeal)

  const { goToFrameSettings } = useStartOrderConfig()
  const toast = useToast()
  const cancelExtraResults = useCancelExtraResults()
  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      if (e.data.action.type !== 'GO_BACK') {
        return
      }
      if (bundleType === 'base') {
        resetItemCompleted()
        return
      }
      removeByBundle('extra')
      cancelExtraResults()
      setDeal(undefined, 'extra')
    })
    return unsubscribe
  }, [
    bundleType,
    cancelExtraResults,
    removeByBundle,
    resetItemCompleted,
    setDeal,
    navigation,
  ])

  const utils = trpc.useUtils()

  const {
    data,
    isLoading,
    isRefetching,
    isFetched,
    isFetching,
    isInitialLoading,
    refetch,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = trpc.frames.customer.list.useInfiniteQuery(
    {
      limit: 6,
      customer_id: customer?.id?.toString(),
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.meta.nextCursor
      },
      enabled: focused && !!customer?.id,
    }
  )

  const handleRefresh = () => {
    // Keep only the 1st page
    utils.frames.customer.list.setInfiniteData(
      {
        limit: 10,
      },
      (data) => ({
        pages: data?.pages.slice(0, 1) ?? [],
        pageParams: data?.pageParams.slice(0, 1) ?? [],
      })
    )

    return refetch()
  }

  useEffect(() => {
    if (!error) {
      return
    }
    captureException(error, {
      tags: {
        location: 'OrderFrames',
      },
    })
    if (error.data?.code === 'UNAUTHORIZED') {
      // FIXME: Not sure why this is firing
      return
    }
    toast({
      title: t('toasts:error.default.title'),
      text: t('toasts:error.default.desc'),
      variant: 'error',
    })
  }, [error, t, toast])

  const remove = trpc.frames.customer.remove.useMutation({
    onSuccess: () => {
      utils.frames.customer.list.invalidate()
    },
  })

  const handleConfirmRemove = () => {
    if (!removeConfirm.data) {
      return
    }
    remove.mutate({ id: removeConfirm.data })
    removeConfirm.close()
  }

  const { isRefetchingByUser, refetchByUser } = useRefreshByUser(
    handleRefresh,
    isRefetching
  )

  const flatData = useFlattenInfiniteData(data)
  const orderItems = useOrderState((s) => s.order.items).filter(
    (i) => i.bundleType === bundleType
  )

  const index = getCurrentFrameIndex(orderItems)
  const selectedFrame = orderItems[index]
  // const framesSelected =
  // const framesTotal = useSession((s) => s.deals[dealKey]?.frames || 1)

  const selectedFrameId = selectedFrame?.frame?.id

  const focus = useIsFocused()
  const list = useRef<FlashListRef<any>>(null)

  useEffect(() => {
    if (!focus) {
      return
    }
    list.current?.scrollToIndex({
      index: 0,
      animated: true,
    })
  }, [focus])

  const showLoading =
    (isFetching || isInitialLoading || isLoading) &&
    !isFetchingNextPage &&
    !isRefetchingByUser
  const showEmpty =
    !isLoading && isFetched && (!flatData || flatData.length === 0)

  const styleEmpty = useAnimatedOpacity(showEmpty)

  const showItems = isFetched
  const styleLoading = useAnimatedOpacity(showLoading)
  const styleItems = useAnimatedOpacity(showItems)

  const { toggleSelect } = useFrameActions({
    flowName: activeDeal?.settings?.flow,
  })

  const removeConfirm = useModalDataState<string | null>({ data: null })

  // On web this keeps rerendering list items on every order state change
  // Can even crash the app in some edge cases
  // if (!focus && Platform.OS === 'web') {
  //   return null
  // }

  return (
    <>
      <Page validateStaff stage="frame" list>
        <FlashList
          ListHeaderComponent={
            <>
              <Header
                list
                title={t(`${text}.header.title`)}
                desc={t(`${text}.header.desc`)}
              />
              <Collapsible open={showLoading} fullWidth>
                <LoadingFooter
                  style={styleLoading}
                  // className="mb-8 h-[175px] p-0"
                />
              </Collapsible>
            </>
          }
          ListFooterComponent={
            <>
              {isFetchingNextPage && !isRefetchingByUser ? (
                <LoadingFooter />
              ) : null}
              <SpaceButtons />
            </>
          }
          data={flatData}
          onRefresh={refetchByUser}
          keyboardShouldPersistTaps="handled"
          refreshing={isRefetchingByUser}
          onEndReached={hasNextPage ? fetchNextPage : undefined}
          ListEmptyComponent={
            <AnimatedEmpty style={styleEmpty}>
              <Search />
            </AnimatedEmpty>
          }
          extraData={selectedFrameId}
          renderItem={({ item }) => {
            return (
              <Animated.View style={styleItems} className="px-8 pb-8">
                <Item
                  onSelect={toggleSelect}
                  isSelected={selectedFrameId === item.frame.id}
                  data={item}
                  onRemove={removeConfirm.open}
                  orderValues={orderValues}
                />
              </Animated.View>
            )
          }}
        />
        {/* </View> */}
        {/* <SpaceButtons height={240} /> */}
      </Page>
      <ButtonsFixed
        buttons={[
          {
            variant: 'outlined',
            label: t('ui:addFrame'),
            onPress: () =>
              router.push({
                pathname: '/frame/search',
                params: { bundleType },
              }),
          },
          {
            disabled: selectedFrameId == null,
            variant: 'filled',
            label: t('ui:nextStep'),
            iconEnd: 'arrow-narrow-right',
            onPress: () =>
              selectedFrameId && goToFrameSettings(selectedFrameId),
          },
        ]}
      />
      {/* <DevStatuses
        booleans={{
          isLoading,
          isFetched,
          isFetching,
          isRefetching,
          isInitialLoading,
        }}
      /> */}
      <DealsModal
        open={dealModal && !isAvatarFlow}
        onSelect={() => {
          setDealModal(false)
          return true
        }}
        close={
          activeDeal
            ? () => {
                setDealModal(false)
              }
            : undefined
        }
      />
      <ConfirmationModal
        open={removeConfirm.isOpen}
        onClose={removeConfirm.close}
        onConfirm={handleConfirmRemove}
        title={t('modals:frame.remove.title')}
        desc={t('modals:frame.remove.desc')}
      />
      <ConfigDebugModal />
    </>
  )
}
